import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Building2, MessageSquare } from "lucide-react";
import HandshakeIcon from "../../shared/icons/HandshakeIcon";
import UsersIcon from "../../shared/icons/UsersIcon";
import style from "./index.module.scss";
import { NOTIFICATION_TYPES } from "../../../mock";
import PartnerIcon from "../../shared/icons/PartnerIcon";

const NotificationCard = ({ notification, onMarkAsRead, isSelected, onSelect, onOpenChat }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getIcon = (type) => {
    switch (type) {
      case "PARTNERSHIP":
        return <HandshakeIcon />;
      case "MESSAGES":
        return <MessageSquare size={24} color="#212121" />;
      case "COMPANY":
        return <Building2 size={24} color="#212121" />;
      case "EMPLOYEES":
        return <UsersIcon />;
      case "MARKETPLACE":
        return <PartnerIcon />;
      default:
        return <MessageSquare size={24} color="#212121" />;
    }
  };

  const handleCardClick = async () => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }

    if (notification.type === "MESSAGES" && notification.orderId && onOpenChat) {
      const serviceType = notification.orderType === "AIRORDER" ? "aviation" : "order";
      onOpenChat(notification.orderId, serviceType);
      return;
    }

    const notificationType = NOTIFICATION_TYPES(t)[notification.type];
    if (notificationType?.link) {
      navigate(notificationType.link);
    }
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    onSelect(notification.id);
  };

  const formatTime = (date) => {
    if (!date) return "";

    const notificationDate = new Date(date);
    const now = new Date();
    const diffInHours = (now - notificationDate) / (1000 * 60 * 60);

    if (notificationDate.getFullYear() !== now.getFullYear()) {
      const day = String(notificationDate.getDate()).padStart(2, "0");
      const month = String(notificationDate.getMonth() + 1).padStart(2, "0");
      const year = notificationDate.getFullYear();

      return `${day}.${month}.${year}`;
    }

    if (diffInHours > 24) {
      const day = notificationDate.getDate();
      const monthNames = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
      const month = monthNames[notificationDate.getMonth()];

      return `${day} ${t(`common.datetime.short_months_lowercase.${month}`)}.`;
    }

    return notificationDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const formattedTime = formatTime(notification.createdAt);

  return (
    <div
      className={`${style.notificationCard} ${!notification.isRead ? style.unread : ""}`}
      onClick={handleCardClick}
    >
      <div className={style.checkboxWrapper} onClick={handleCheckboxChange}>
        <input
          type="checkbox"
          className={style.checkbox}
          checked={isSelected}
          onChange={handleCheckboxChange}
        />
        <div className={style.checkboxCustom}>
          {isSelected && (
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      </div>

      <div className={style.iconWrapper}>{getIcon(notification.type)}</div>

      <div className={style.content}>
        <div className={style.header}>
          <h4 className={style.title}>{notification.title}</h4>
          <span className={style.time}>{formattedTime}</span>
        </div>
        <p className={style.text}>{notification.text}</p>
      </div>

      {!notification.isRead && <div className={style.unreadIndicator} />}
    </div>
  );
};

export default NotificationCard;
